import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

interface Props {
  open: boolean;
  onClose: () => void;
  record: any;
}

// Helper: Format ISO timestamp to readable time
const formatTime = (isoString: string) => {
  if (!isoString) return '-';
  return new Date(isoString).toLocaleTimeString('en-US', {
    hour12: false,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZone: 'America/New_York'
  });
};

const PolicyTimingDialog: React.FC<Props> = ({ open, onClose, record }) => {
  const [index, setIndex] = useState(0);
  useEffect(() => {
    setIndex(0);
  }, [record]);
  // Safety check
  if (!open || !record || !Array.isArray(record.Policy_Timing)) {
  return null;
  }


  const policies = record.Policy_Timing;
  const currentPolicy = policies[index];
  if (!currentPolicy || typeof currentPolicy !== 'object') {
    return null;
  }
  // Logic: Extract only step objects, ignore metadata, and sort by start time
  const stepEntries = Object.entries(currentPolicy)
    .filter(([key, val]: any) => {
      // ❌ Exclude Metadata & Totals
      if (key === 'appnum' || key === 'sub_workflow_id' || key === 'TOTAL_POLICY_RUNTIME_SECONDS') return false;
      // ✅ Include only objects with duration/start/end
      return typeof val === 'object' && val !== null;
    })
    .sort((a: any, b: any) => {
      // Sort by start so the steps appear in order
      const startA = a[1].start || '';
      const startB = b[1].start || '';
      return startA.localeCompare(startB);
    });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ borderBottom: '1px solid rgba(255,255,255,0.1)', pb: 2 }}>
        Policy Timing Breakdown
        <IconButton onClick={onClose} sx={{ float: 'right', mt: -1 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ mt: 2 }}>
        {/* 🟢 SECTION 1: Policy Selector (Chips) */}
        <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
          <Typography variant="subtitle2" sx={{ color: 'text.secondary', mr: 1 }}>
            Policy / App Num:
          </Typography>
          {policies.map((p: any, i: number) => (
            <Chip
              key={p.appnum || i}
              label={p.appnum}
              color={i === index ? 'success' : 'default'}
              variant={i === index ? 'filled' : 'outlined'}
              onClick={() => setIndex(i)}
              sx={{ cursor: 'pointer', fontWeight: i === index ? 600 : 400 }}
            />
          ))}
        </Box>

        {/* 🟢 SECTION 2: Clean Step Table */}
        <TableContainer component={Paper} variant="outlined" sx={{ bgcolor: 'transparent' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'rgba(23, 175, 85, 0.08)' }}>
                <TableCell sx={{ fontWeight: 600 }}>Step Name</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Start Time</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>End Time</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">Duration (s)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {stepEntries.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">No timing steps available</TableCell>
                </TableRow>
              ) : (
                stepEntries.map(([key, val]: any) => (
                  <TableRow key={key} hover>
                    <TableCell sx={{ textTransform: 'capitalize' }}>
                      {key.replace(/_/g, ' ')}
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                      {formatTime(val.start)}
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                      {formatTime(val.end)}
                    </TableCell>
                    <TableCell 
                      align="right" 
                      sx={{ 
                        fontWeight: 600, 
                        color: val.duration_seconds > 5 ? 'warning.main' : 'text.primary' 
                      }}
                    >
                      {val.duration_seconds ? val.duration_seconds.toFixed(2) : '-'}s
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
    </Dialog>
  );
};

export default PolicyTimingDialog;